import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { formatPaise, maskAadhaar, toPaise } from '@pharmacia/core'
import { ApiError, del, get } from '../lib/api.ts'
import { formatDate, formatDateTime } from '../lib/format.ts'
import { useAuth } from '../lib/auth.tsx'
import { PageHeader } from '../components/AppShell.tsx'
import { ConfirmDialog } from '../components/ConfirmDialog.tsx'
import { useToast } from '../components/Toast.tsx'
import { Button, Empty, Skeleton } from '../components/ui.tsx'
import { BillStatus } from '../components/BillStatus.tsx'
import { IconBilling, IconEdit, IconEye, IconRx, IconTrash } from '../components/icons.tsx'

type Detail = {
  patient: Record<string, unknown>
  registrations: Record<string, unknown>[]
  measurements: Record<string, unknown>[]
  files: { id: string; kind: string; mime_type: string }[]
}

const DAYS = [0, 5, 10, 15, 20, 25, 30]

type RxRow = {
  id: string; number: string; prescribedAt: string; status: 'draft' | 'sent'
  doctorName: string; lineCount: number; dayCount: number
  billState: 'none' | 'with_counter' | 'issued' | 'paid'
}
type BillRow = {
  id: string; number: string | null; status: 'draft' | 'final' | 'cancelled'; total: string | null
  estimated: boolean; issuedAt: string | null; createdAt: string
  paidAt: string | null; paidAmount: string | null; paymentMethod: string | null
  prescriptionNumber: string
}

const rxState = (r: RxRow) =>
  r.billState === 'paid' ? { label: 'Paid', cls: 'final' }
  : r.billState === 'issued' ? { label: 'Billed', cls: 'doctor' }
  : r.status === 'sent' ? { label: 'With counter', cls: 'reception' }
  : { label: 'Draft', cls: 'draft' }

function Value({ k, v }: { k: string; v: unknown }) {
  const empty = v === null || v === undefined || v === ''
  return (
    <div>
      <span className="k">{k}</span>
      <span className={`v ${empty ? 'empty' : ''}`}>{empty ? '—' : String(v)}</span>
    </div>
  )
}

export function PatientDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { can } = useAuth()

  const [data, setData] = useState<Detail | null>(null)
  const [urls, setUrls] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [confirming, setConfirming] = useState(false)
  const [rx, setRx] = useState<{ items: RxRow[]; scoped: boolean } | null>(null)
  const [bills, setBills] = useState<BillRow[] | null>(null)

  // Fetched separately and only where the role allows it: reception may
  // open a patient but not read prescriptions, and a doctor the reverse.
  // Folding both into the patient record would have leaked one to the
  // other.
  useEffect(() => {
    let cancelled = false
    if (can('prescription:read')) {
      get<{ items: RxRow[]; scoped: boolean }>(`/api/patients/${id}/prescriptions`)
        .then(d => { if (!cancelled) setRx(d) }).catch(() => {})
    }
    if (can('billing:read')) {
      get<{ items: BillRow[] }>(`/api/patients/${id}/bills`)
        .then(d => { if (!cancelled) setBills(d.items) }).catch(() => {})
    }
    return () => { cancelled = true }
  }, [id, can])

  // The record renders as soon as it arrives. Awaiting the photo here as
  // well held the entire page on a skeleton until a second round trip
  // finished -- for an image that sits in one corner of it.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    get<Detail>(`/api/patients/${id}`)
      .then(d => { if (!cancelled) setData(d) })
      .catch((e: ApiError) => { if (!cancelled) toast.fail('Could not load patient', e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id, toast])

  // The bucket is private, so every image comes through a short-lived
  // signed link issued only after the API has checked this person may see
  // it. Fetched together rather than one after another.
  useEffect(() => {
    if (!data?.files.length) return
    let cancelled = false
    void Promise.all(data.files.map(async f => {
      try {
        const { url } = await get<{ url: string }>(`/api/files/${f.id}`)
        if (!cancelled) setUrls(prev => ({ ...prev, [f.kind]: url }))
      } catch { /* the record is still readable without it */ }
    }))
    return () => { cancelled = true }
  }, [data])

  if (loading) {
    return (
      <>
        <PageHeader title="Patient" />
        <div className="content"><div className="page-max card"><div className="card-body stack" style={{ gap: 'var(--s-4)' }}>
          {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} h={22} />)}
        </div></div></div>
      </>
    )
  }
  if (!data) return null

  const p = data.patient as Record<string, unknown>
  const reg = data.registrations[0]
  const forReg = data.measurements.filter(m => !reg || m['registration_id'] === reg['id'])

  return (
    <>
      <PageHeader title={String(p['fullName'])}>
        <span className="mono dim">{String(p['mrn'])}</span>
        {can('patient:write') ? (
          <>
            <Button onClick={() => navigate(`/patients/${id}/edit`)}><IconEdit /> Edit</Button>
            <Button variant="danger" onClick={() => setConfirming(true)}><IconTrash /> Delete</Button>
          </>
        ) : null}
      </PageHeader>

      <div className="content">
        <div className="page-max stack" style={{ gap: 'var(--s-5)', maxWidth: 900 }}>

          <div className="card">
            <div className="card-head"><h3>Identification</h3></div>
            <div className="card-body row" style={{ gap: 'var(--s-6)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div className="photo-box">
                {urls['photo'] ? <img src={urls['photo']} alt={String(p['fullName'])} /> : <span>No photo</span>}
              </div>
              <div className="grow detail-grid">
                {/* The name is in the page header, but a record printed or
                    read on its own must carry it too. */}
                <Value k="Name" v={p['fullName']} />
                <Value k="MRN" v={p['mrn']} />
                <Value k="Father's name" v={p['fatherName']} />
                <Value k="Age" v={p['age']} />
                <Value k="Sex" v={p['sex']} />
                <Value k="Blood group" v={p['bloodGroup']} />
                <Value k="Marital status" v={p['maritalStatus']} />
                <Value k="Education" v={p['education']} />
                <Value k="Caste" v={p['caste']} />
                <Value k="Occupation" v={p['occupation']} />
                <Value k="Phone" v={p['phone']} />
                <Value k="Address" v={p['addressLine1']} />
                <Value k="Tehsil" v={p['tehsil']} />
                <Value k="District" v={p['district']} />
                <Value k="State" v={p['state']} />
                <Value k="Pincode" v={p['pincode']} />
                {/* Only the last four digits. Section 29(4) of the Aadhaar
                    Act forbids displaying the number, and the full value is
                    available on the edit screen when it is actually needed. */}
                <Value k="Aadhaar" v={p['aadhaar'] ? maskAadhaar(String(p['aadhaar'])) : null} />
                <Value k="Registered" v={formatDate(p['createdAt'])} />
              </div>
            </div>
          </div>

          {urls['aadhaar_front'] || urls['aadhaar_back'] ? (
            <div className="card">
              <div className="card-head"><h3>Aadhaar card</h3></div>
              <div className="card-body capture-pair">
                <div>
                  <div className="k" style={{ marginBottom: 'var(--s-2)' }}>Front</div>
                  <div className="doc-shot">
                    {urls['aadhaar_front']
                      ? <img src={urls['aadhaar_front']} alt="Aadhaar front" />
                      : <span>Not captured</span>}
                  </div>
                </div>
                <div>
                  <div className="k" style={{ marginBottom: 'var(--s-2)' }}>Back</div>
                  <div className="doc-shot">
                    {urls['aadhaar_back']
                      ? <img src={urls['aadhaar_back']} alt="Aadhaar back" />
                      : <span>Not captured</span>}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {reg ? (
            <>
              <div className="card">
                <div className="card-head">
                  <h3 className="grow">Register entry</h3>
                  <span className="mono dim">{String(reg['serial_no'])}</span>
                </div>
                <div className="card-body detail-grid">
                  <Value k="Quantity per month"
                    v={reg['opiate_amount']
                      ? `${Number(reg['opiate_amount'])} ${reg['opiate_unit'] === 'kg' ? 'Kg' : reg['opiate_unit']}`
                        + (reg['opiate_substance'] ? `, ${String(reg['opiate_substance'])}` : '')
                      : null} />
                  <Value k="Any tablet" v={reg['tablets']} />
                  <Value k="Addiction period"
                    v={reg['addiction_years'] != null || reg['addiction_months'] != null
                      ? `${reg['addiction_years'] ?? 0} yr ${reg['addiction_months'] ?? 0} mo` : null} />
                  <Value k="Remarks" v={reg['remarks']} />
                  <Value k="Declaration" v={reg['declaration_version']} />
                  <Value k="Entered" v={formatDateTime(reg['registered_at'])} />
                </div>
              </div>

              <div className="card">
                <div className="card-head"><h3>Thirty-day readings</h3></div>
                <div className="table-wrap">
                  <table className="data">
                    <thead><tr>
                      <th>Day</th><th>Weight</th><th>BP</th><th>Pulse</th><th>SpO₂</th><th>Recorded</th>
                    </tr></thead>
                    <tbody>
                      {DAYS.map(day => {
                        const m = forReg.find(x => Number(x['day_offset']) === day)
                        return (
                          <tr key={day}>
                            <td style={{ fontWeight: 550 }}>{day === 0 ? 'At registration' : `Day ${day}`}</td>
                            <td>{m?.['weight_kg'] ? `${Number(m['weight_kg'])} kg` : <span className="dim">—</span>}</td>
                            <td>{m ? `${m['bp_systolic']}/${m['bp_diastolic']}` : <span className="dim">—</span>}</td>
                            <td>{m ? String(m['pulse']) : <span className="dim">—</span>}</td>
                            <td>{m ? `${m['spo2']}%` : <span className="dim">—</span>}</td>
                            <td className="dim">{m ? formatDate(m['recorded_at']) : 'not yet taken'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card">
                <div className="card-head"><h3>Declaration signed</h3></div>
                <div className="card-body">
                  <div className="declaration">
                    {String(reg['declaration_body'])}
                    <span className="ver">Version {String(reg['declaration_version'])}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="card"><div className="empty">
              <h3>No register entry</h3>
              <p className="muted">This patient has no NDPS register entry.</p>
            </div></div>
          )}

          {rx ? (
            <div className="card">
              <div className="card-head">
                <h3 className="grow">Prescriptions</h3>
                {rx.scoped ? (
                  <span className="dim" style={{ fontSize: 'var(--text-xs)' }}>
                    showing the ones you wrote
                  </span>
                ) : null}
              </div>
              {rx.items.length === 0 ? (
                <Empty title="None yet">
                  {rx.scoped ? 'You have not written one for this patient.'
                    : 'This patient has no prescriptions.'}
                </Empty>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead><tr>
                      <th>Number</th><th>Doctor</th><th>Contents</th>
                      <th>Status</th><th>Written</th><th />
                    </tr></thead>
                    <tbody>
                      {rx.items.map(r => (
                        <tr key={r.id}>
                          <td className="mono">{r.number}</td>
                          <td className="muted">{r.doctorName}</td>
                          <td>
                            {r.lineCount} item{r.lineCount === 1 ? '' : 's'}
                            {r.dayCount > 0 ? (
                              <span className="dim"> · {r.dayCount} day{r.dayCount === 1 ? '' : 's'}</span>
                            ) : null}
                          </td>
                          <td><span className={`badge ${rxState(r).cls}`}>{rxState(r).label}</span></td>
                          <td className="dim">{formatDate(r.prescribedAt)}</td>
                          <td className="actions">
                            <button className="icon-btn" title="Open prescription"
                              aria-label={`Open ${r.number}`}
                              onClick={() => navigate(`/prescriptions/${r.id}`)}><IconEye /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {bills ? (
            <div className="card">
              <div className="card-head"><h3 className="grow">Bills</h3></div>
              {bills.length === 0 ? (
                <Empty title="None yet">Nothing has been billed for this patient.</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead><tr>
                      <th>Bill</th><th>Prescription</th><th>Status</th>
                      <th className="money">Amount</th><th>Date</th><th />
                    </tr></thead>
                    <tbody>
                      {bills.map(b => (
                        <tr key={b.id}>
                          <td className="mono">
                            {b.number ?? <span className="dim">— draft —</span>}
                          </td>
                          <td className="muted mono" style={{ fontSize: 'var(--text-xs)' }}>
                            {b.prescriptionNumber}
                          </td>
                          <td>
                            <BillStatus status={b.status} paidAt={b.paidAt} />
                          </td>
                          <td className="money" style={{ fontWeight: 650 }}>
                            {b.total ? formatPaise(toPaise(b.total)) : <span className="dim">—</span>}
                            {b.estimated ? (
                              <div className="dim" style={{ fontSize: 'var(--text-xs)', fontWeight: 400 }}>
                                not yet issued
                              </div>
                            ) : null}
                          </td>
                          <td className="dim">{formatDate(b.issuedAt ?? b.createdAt)}</td>
                          <td className="actions">
                            <button className="icon-btn" title="Open bill" aria-label="Open bill"
                              onClick={() => navigate(`/billing/${b.id}`)}><IconEye /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {confirming ? (
        <ConfirmDialog
          title={`Delete ${p['fullName']}?`}
          body={`The record is kept, not erased — ${p['mrn']} stays issued and the audit trail keeps pointing at it. It stops appearing in the patient list.`}
          confirmLabel="Delete"
          danger
          onClose={() => setConfirming(false)}
          onConfirm={async () => {
            try {
              await del(`/api/patients/${id}`)
              toast.ok('Patient deleted')
              navigate('/patients')
            } catch (e) { toast.fail('Could not delete', (e as ApiError).message) }
          }}
        />
      ) : null}
    </>
  )
}
